import { useSyncExternalStore } from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { Button } from '../../tailwindComponents/Button';

const DISMISSED_STORAGE_KEY = 'floxPalletStackingExplainerDismissed';

const readDismissed = (): boolean => {
  try {
    return localStorage.getItem(DISMISSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const subscribers = new Set<() => void>();
let isDismissed = readDismissed();

const subscribe = (onChange: () => void) => {
  subscribers.add(onChange);
  return () => {
    subscribers.delete(onChange);
  };
};

const dismissEverywhere = () => {
  isDismissed = true;
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, '1');
  } catch {
    /* private mode — dismissal just won't persist */
  }
  subscribers.forEach((onChange) => onChange());
};

function PalletLayerFigure() {
  const layerRows = [0, 1];
  return (
    <div
      className='flex shrink-0 flex-col items-center gap-1'
      aria-hidden='true'
    >
      <div className='flex flex-col gap-0.5'>
        {layerRows.map((layerIndex) => (
          <div key={layerIndex} className='flex gap-0.5'>
            {[0, 1, 2].map((unitIndex) => (
              <span
                key={unitIndex}
                className='h-2.5 w-2.5 rounded-[2px] border border-blue-200 bg-white shadow-sm'
              />
            ))}
          </div>
        ))}
      </div>
      <div className='h-1.5 w-12 rounded-sm bg-amber-600/60' />
      <div className='flex w-12 justify-between px-1'>
        <span className='h-1 w-1.5 bg-amber-600/60' />
        <span className='h-1 w-1.5 bg-amber-600/60' />
      </div>
      <span className='text-xs text-gray-500'>3 × 2 layers = 6</span>
    </div>
  );
}

export function PalletStackingExplainer() {
  const dismissed = useSyncExternalStore(subscribe, () => isDismissed);

  if (dismissed) return null;

  return (
    <div className='rounded-lg border border-blue-100 bg-blue-50/60 p-4 font-inter'>
      <div className='flex items-start gap-4'>
        <PalletLayerFigure />
        <div className='min-w-0 flex-1'>
          <div className='flex items-start justify-between gap-3'>
            <p className='text-sm font-semibold text-gray-900'>
              How pallet stacking works
            </p>
            <button
              type='button'
              onClick={dismissEverywhere}
              aria-label='Dismiss pallet stacking explainer'
              className='shrink-0 rounded-md p-1 text-gray-400 transition hover:bg-blue-100 hover:text-gray-600'
            >
              <XMarkIcon className='h-5 w-5' />
            </button>
          </div>
          <p className='mt-1 text-sm text-gray-600'>
            Enter how many sit on one layer and how many layers stack up — we
            work out the per-pallet totals. Palletised more than one way? Add a
            configuration for each: for example how goods arrive at the
            warehouse and how you despatch them can be different.
          </p>
          <div className='mt-2'>
            <Button
              intent='secondary'
              size='sm'
              label='Got it'
              onClick={dismissEverywhere}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
